/** Rotate / shuffle zone colors and build expert palette cycles for small fixture counts. */

export const ZONE_CYCLE_MAX_ZONES = 4;

export function cloneColor(c) {
	return { r: c.r, g: c.g, b: c.b };
}

/** Preserve first-seen order while dropping duplicate swatches. */
export function extractPaletteInOrder(colors) {
	const seen = new Set();
	const out = [];
	for (const c of colors || []) {
		const key = `${c.r},${c.g},${c.b}`;
		if (!seen.has(key)) {
			seen.add(key);
			out.push(cloneColor(c));
		}
	}
	return out;
}

/** Shift zone colors left by `shift` positions (default 1). */
export function rotateColors(colors, shift = 1) {
	if (!colors?.length) return [];
	const n = colors.length;
	const s = ((shift % n) + n) % n;
	return [...colors.slice(s), ...colors.slice(0, s)].map(cloneColor);
}

/** Randomly reassign palette colors to zones (Fisher–Yates shuffle). */
export function shuffleColorsToZones(colors) {
	if (!colors?.length) return [];
	const out = colors.map(cloneColor);
	for (let i = out.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[out[i], out[j]] = [out[j], out[i]];
	}
	return out;
}

export function countDistinctColors(colors) {
	const keys = new Set((colors || []).map((c) => `${c.r},${c.g},${c.b}`));
	return keys.size;
}

/**
 * Build a cycle where each step shifts expert palette colors across zones.
 * For a single zone, each look shows the next palette color in sequence.
 */
export function buildZoneRotationCycle(paletteColors, numZones, hold = 30) {
	const palette = extractPaletteInOrder(paletteColors);
	if (!palette.length || !numZones) return null;

	const steps = palette.length;
	const looks = [];
	for (let step = 0; step < steps; step++) {
		const lookColors = [];
		for (let zone = 0; zone < numZones; zone++) {
			lookColors.push(cloneColor(palette[(step + zone) % palette.length]));
		}
		looks.push({ colors: lookColors, hold });
	}
	return { type: 'cycle', looks };
}

/** Offer zone-rotation cycle when fixtures are few and the palette has distinct colors. */
export function shouldOfferZoneRotationCycle(numZones, preview) {
	if (!preview || preview.type !== 'look') return false;
	if (!numZones || numZones > ZONE_CYCLE_MAX_ZONES) return false;
	const distinct = countDistinctColors(preview.colors);
	return distinct >= 2;
}

export function getZoneRotationCycleStepCount(preview) {
	return extractPaletteInOrder(preview?.colors).length;
}
